/**
 * Pure utility function for parsing requalification activity changes.
 * Returns structured data for flexible rendering.
 *
 * Note: src/components/Feed/FeedRequalification.tsx is a legacy Relay component
 * with similar logic but tightly coupled to rendering. This utility provides
 * a cleaner separation of parsing logic from presentation.
 */
import type { RequalificationChange } from '@modules/_shared/components/feed-v2/types';
import { getRatingLabel } from '@modules/_shared/utils/formatting';
import {
  translateAppraisalReason,
  translateBuyHorizon,
  translateMortgageBuyingStatus,
  translateMortgageStatus,
  translateOpenToOffer,
  translatePropertyOccupied,
  translatePropertyUsage,
  translateReceivedMortgageOffer,
  translateRelationship,
  translateSaleHorizon,
  translateSellingWith,
} from '@modules/_shared/utils/lead-labels';
import type { Translate } from '@shared/hooks/locale';

const translateValue = (key: string, value: unknown, t: Translate) => {
  if (value == null) {
    return t('Not set');
  }
  const strValue = typeof value === 'string' ? value : String(value);

  switch (key) {
    case 'relationship':
      return translateRelationship(t, strValue) ?? strValue;
    case 'appraisal_reason':
      return translateAppraisalReason(t, strValue) ?? strValue;
    case 'sale_horizon':
      return translateSaleHorizon(t, strValue) ?? strValue;
    case 'buy_horizon':
      return translateBuyHorizon(t, strValue) ?? strValue;
    case 'mortgage_buying_status':
      return translateMortgageBuyingStatus(t, strValue) ?? strValue;
    case 'mortgage_status':
      return translateMortgageStatus(t, strValue) ?? strValue;
    case 'received_mortgage_offer':
      return translateReceivedMortgageOffer(t, !!strValue) ?? strValue;
    case 'appraisal_open_to_offer':
      return translateOpenToOffer(t, strValue) ?? strValue;
    case 'appraisal_property_occupied':
      return translatePropertyOccupied(t, strValue) ?? strValue;
    case 'appraisal_property_usage':
      return translatePropertyUsage(t, strValue) ?? strValue;
    case 'appraisal_selling_with':
      return translateSellingWith(t, strValue) ?? strValue;
    case 'appraisal_property_building_condition':
      return getRatingLabel(value as number | string, t) ?? strValue;
    default:
      return strValue;
  }
};

export const parseRequalificationChanges = (
  extraData: Record<string, unknown> | null | undefined,
  t: Translate,
) => {
  if (!extraData?.updatedValues) {
    return [];
  }

  const updatedValues = extraData.updatedValues;
  const keysOrder = [
    'relationship',
    'appraisal_reason',
    'sale_horizon',
    'buy_horizon',
    'mortgage_buying_status',
    'mortgage_status',
    'received_mortgage_offer',
    'perceived_value',
    'appraisal_open_to_offer',
    'appraisal_property_occupied',
    'appraisal_property_usage',
    'appraisal_property_building_condition',
    'appraisal_selling_with',
  ];

  const fieldLabels: Record<string, string> = {
    relationship: t('Relationship'),
    appraisal_reason: t('Appraisal reason'),
    buy_horizon: t('Buying horizon'),
    sale_horizon: t('Sale horizon'),
    mortgage_buying_status: t('Buying stage'),
    mortgage_status: t('Mortgage application'),
    received_mortgage_offer: t('Mortgage offer'),
    appraisal_open_to_offer: t('Open to offer'),
    appraisal_property_occupied: t('Occupancy'),
    appraisal_property_usage: t('Property usage'),
    perceived_value: t('Perceived value'),
    appraisal_property_building_condition: t('Building condition'),
    appraisal_selling_with: t('Selling with'),
  };

  const changes: RequalificationChange[] = [];
  const updatedValuesRecord = updatedValues as Record<
    string,
    { old?: unknown; new?: unknown }
  >;
  for (const key of keysOrder) {
    if (updatedValuesRecord[key] != null) {
      const oldVal = translateValue(key, updatedValuesRecord[key].old, t);
      const newVal = translateValue(key, updatedValuesRecord[key].new, t);
      if (oldVal !== newVal) {
        changes.push({
          field: fieldLabels[key] || key,
          from: oldVal,
          to: newVal,
        });
      }
    }
  }
  return changes;
};
